import { InjectionToken } from '@angular/core';

/*
 * The runner's api2 calls (/research-survey/check) use survey-client's token from the shared localStorage, which
 * survey-client replaces whenever another questionnaire is opened. So before opening a questionnaire survey-client
 * keeps a copy of its token under `<shared prefix>scoped-<session id>-`, and the runner points AuthService at that
 * copy through APP_CONFIG.localStorageAuthPrefix2.
 */

/* what AuthService stores under its localStorage prefix */
const AUTH_KEYS = ['token', 'tokenExpiration', 'refresh_token', 'refreshTokenExpiration'];
const SCOPED_KEY_PREFIX = 'scoped-';
const MAX_IDLE_DAYS = 30;

export interface SurveySessionAuth {
  /* where survey-client keeps its token */
  sharedPrefix: string;
  /* where this questionnaire's copy is, sharedPrefix when survey-client didn't keep one */
  sessionPrefix: string;
}

export const SURVEY_SESSION_AUTH = new InjectionToken<SurveySessionAuth>('SURVEY_SESSION_AUTH');

/** survey-client: keeps a copy of its current token for the questionnaire it is about to open */
export function keepAuthTokensForSurvey(sharedPrefix: string, sessionId: string): void {
  removeIdleSurveyAuthTokens(sharedPrefix);
  copyAuthTokens(sharedPrefix, getSessionPrefix(sharedPrefix, sessionId));
}

/** runner: the questionnaire at `pathname` uses its own copy of the token, if survey-client kept one */
export function getSurveySessionAuth(sharedPrefix: string, baseHref: string, pathname: string): SurveySessionAuth {
  /* DecisionTreeModule's routes start with ':id' */
  const sessionId = pathname.startsWith(baseHref) ? pathname.slice(baseHref.length).split('/')[0] : '';
  const sessionPrefix = getSessionPrefix(sharedPrefix, sessionId);
  return {
    sharedPrefix,
    sessionPrefix: sessionId && hasAuthTokens(sessionPrefix) ? sessionPrefix : sharedPrefix,
  };
}

/** runner: before leaving for survey-client, which reads the shared token */
export function shareSurveyAuthTokens({ sessionPrefix, sharedPrefix }: SurveySessionAuth): void {
  copyAuthTokens(sessionPrefix, sharedPrefix);
}

function getSessionPrefix(sharedPrefix: string, sessionId: string): string {
  return `${sharedPrefix}${SCOPED_KEY_PREFIX}${sessionId}-`;
}

function hasAuthTokens(prefix: string): boolean {
  return !!localStorage.getItem(`${prefix}refresh_token`);
}

function copyAuthTokens(fromPrefix: string, toPrefix: string): void {
  if (fromPrefix === toPrefix || !hasAuthTokens(fromPrefix)) return;
  AUTH_KEYS.forEach((key) => {
    const value = localStorage.getItem(`${fromPrefix}${key}`);
    if (value === null) {
      localStorage.removeItem(`${toPrefix}${key}`);
    } else {
      localStorage.setItem(`${toPrefix}${key}`, value);
    }
  });
}

/* AuthService moves tokenExpiration on every refresh, so it tells when a copy was last used */
function removeIdleSurveyAuthTokens(sharedPrefix: string): void {
  const scopedKeyStart = `${sharedPrefix}${SCOPED_KEY_PREFIX}`;
  const idleSince = Date.now() - MAX_IDLE_DAYS * 24 * 60 * 60 * 1000;
  Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
    .filter((key) => key.startsWith(scopedKeyStart) && key.endsWith('-refresh_token'))
    .map((key) => key.slice(0, -'refresh_token'.length))
    .filter((prefix) => Number(localStorage.getItem(`${prefix}tokenExpiration`)) < idleSince)
    .forEach((prefix) => AUTH_KEYS.forEach((key) => localStorage.removeItem(`${prefix}${key}`)));
}
